import { getTreatmentLaunchContext } from "./launchContext";
import { normalizeRuntimeApiBaseUrl } from "./runtimeApiBase";

export const QUESTIONNAIRE_LAUNCH_MODE = Object.freeze({
  legacy: "legacy",
  treatmentLive: "treatment_live",
  treatmentPreview: "treatment_preview",
});

export const TREATMENT_LAUNCH_SOURCE_KIND = Object.freeze({
  program: "program",
  customProgram: "custom_program",
});

export const LEGACY_RUNTIME_TYPE = Object.freeze({
  questionnaire: "questionnaire",
});

export class QuestionnaireLaunchError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "QuestionnaireLaunchError";
    this.code = code;
  }
}

const requireValue = (value, code, message) => {
  if (!value) throw new QuestionnaireLaunchError(code, message);
  return value;
};

const freezeLaunch = (launch) => Object.freeze(launch);

export const resolvePreviewLaunch = ({ search = "", hash = "" } = {}) => {
  const context = getTreatmentLaunchContext(search, hash);
  if (!context.isAdminPreview) return null;

  const sourceKind = context.previewCustomProgramId
    ? TREATMENT_LAUNCH_SOURCE_KIND.customProgram
    : TREATMENT_LAUNCH_SOURCE_KIND.program;
  const sourceId = context.previewCustomProgramId
    || context.previewProgramId;

  requireValue(
    sourceId,
    "preview_source_required",
    "Treatment preview source is required.",
  );
  if (context.previewCapabilityToken) {
    requireValue(
      context.previewSnapshotId,
      "preview_snapshot_required",
      "Treatment preview snapshot is required.",
    );
    requireValue(
      context.previewSnapshotChecksum,
      "preview_checksum_required",
      "Treatment preview checksum is required.",
    );
  }

  return freezeLaunch({
    mode: QUESTIONNAIRE_LAUNCH_MODE.treatmentPreview,
    sourceKind,
    sourceId,
    templateId: null,
    previewKind: context.previewKind,
    apiBaseUrl: normalizeRuntimeApiBaseUrl(context.previewApiBaseUrl),
    capabilityToken: context.previewCapabilityToken,
    snapshotId: context.previewSnapshotId,
    snapshotChecksum: context.previewSnapshotChecksum,
    context,
  });
};

export const resolveVisitLaunch = ({
  visitConfig,
  fallbackTemplateId = null,
  runtimeApiBaseUrl = null,
}) => {
  const runtime = visitConfig?.runtime;
  const isTreatmentRuntime = (
    runtime?.type === TREATMENT_LAUNCH_SOURCE_KIND.program
    || runtime?.type === TREATMENT_LAUNCH_SOURCE_KIND.customProgram
  );
  if (isTreatmentRuntime) {
    return freezeLaunch({
      mode: QUESTIONNAIRE_LAUNCH_MODE.treatmentLive,
      sourceKind: runtime.type,
      sourceId: requireValue(
        runtime.id,
        "runtime_source_required",
        "Treatment runtime source is required.",
      ),
      templateId: requireValue(
        runtime.template_id,
        "runtime_template_required",
        "Treatment runtime questionnaire template is required.",
      ),
      releaseId: runtime.release_id || null,
      apiBaseUrl: normalizeRuntimeApiBaseUrl(runtimeApiBaseUrl),
    });
  }
  if (
    runtime?.type
    && runtime.type !== LEGACY_RUNTIME_TYPE.questionnaire
  ) {
    throw new QuestionnaireLaunchError(
      "runtime_type_unsupported",
      `Unsupported questionnaire runtime '${runtime.type}'.`,
    );
  }

  const templateId = visitConfig?.form_template?.id || fallbackTemplateId;
  return freezeLaunch({
    mode: QUESTIONNAIRE_LAUNCH_MODE.legacy,
    sourceKind: null,
    sourceId: null,
    templateId: requireValue(
      templateId,
      "legacy_template_required",
      "No questionnaire template is configured for this visit.",
    ),
    questionnaireType:
      visitConfig?.form_template?.questionnaire_type || null,
  });
};

// Published intake URLs carry the immutable source identity. If an older
// visit-config response omits its runtime block, keep that URL on the live
// runtime path instead of rendering an empty legacy shell.
export const resolveDirectRuntimeLaunch = ({
  programId = null,
  customProgramId = null,
  templateId = null,
  apiBaseUrl = null,
} = {}) => {
  const sourceKind = programId
    ? TREATMENT_LAUNCH_SOURCE_KIND.program
    : customProgramId
      ? TREATMENT_LAUNCH_SOURCE_KIND.customProgram
      : null;
  const sourceId = programId || customProgramId;
  if (!sourceKind || !sourceId) return null;

  return freezeLaunch({
    mode: QUESTIONNAIRE_LAUNCH_MODE.treatmentLive,
    sourceKind,
    sourceId,
    templateId,
    releaseId: null,
    apiBaseUrl: normalizeRuntimeApiBaseUrl(apiBaseUrl),
  });
};

export const resolveDefaultLegacyLaunch = (templateId) => freezeLaunch({
  mode: QUESTIONNAIRE_LAUNCH_MODE.legacy,
  sourceKind: null,
  sourceId: null,
  templateId: requireValue(
    templateId,
    "legacy_template_required",
    "No questionnaire template is configured for this client.",
  ),
  questionnaireType: null,
});

export const launchRuntimeProps = (launch) => {
  if (!launch) return Object.freeze({});
  const isProgram =
    launch.sourceKind === TREATMENT_LAUNCH_SOURCE_KIND.program;
  const isCustomProgram =
    launch.sourceKind === TREATMENT_LAUNCH_SOURCE_KIND.customProgram;

  return Object.freeze({
    templateId: launch.templateId || null,
    runtimeProgramId:
      launch.mode === QUESTIONNAIRE_LAUNCH_MODE.treatmentLive && isProgram
        ? launch.sourceId
        : null,
    runtimeCustomProgramId:
      launch.mode === QUESTIONNAIRE_LAUNCH_MODE.treatmentLive && isCustomProgram
        ? launch.sourceId
        : null,
    previewProgramId:
      launch.mode === QUESTIONNAIRE_LAUNCH_MODE.treatmentPreview && isProgram
        ? launch.sourceId
        : null,
    previewCustomProgramId:
      launch.mode === QUESTIONNAIRE_LAUNCH_MODE.treatmentPreview && isCustomProgram
        ? launch.sourceId
        : null,
    previewApiBaseUrl: launch.apiBaseUrl || null,
    runtimeApiBaseUrl: launch.apiBaseUrl || null,
    previewCapabilityToken: launch.capabilityToken || null,
    previewSnapshotChecksum: launch.snapshotChecksum || null,
    previewSnapshotId: launch.snapshotId || null,
  });
};
